// @givmo/auth — ADMIN auth flows (webapp + super-admin).
//
// Ports the webapp admin auth orchestration — `AuthDatasourceImpl` + `SignInCubit` + `SignUpCubit`
// (givmo-webapp/lib/features/auth/…). The framework-specific cubit/bloc machinery is dropped; the
// PORTABLE logic that the spec calls out — domain pre-check + Firebase-user ROLLBACK on a failed email
// sign-up, backend-OTP email verification (custom code endpoints, NOT Firebase email links), and the
// `pendingManualVerification` routing decision — lives here as injectable functions. One ordering is
// Givmo's own, not the webapp's (NF-FIX21): the email sign-up proves the work email BEFORE it
// registers a company, as the provider path always has.
//
// Firebase and the backend are injected as ports so the orchestration is unit-testable with both
// mocked (the spec's "Vitest tests for the session/rollback/strategy logic (mock Firebase)").

import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  getAdditionalUserInfo,
  type Auth,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { signInWithEmail } from './email';
import { AuthError } from './errors';
import { SecondFactorRequiredError, withSecondFactorChallenge } from './mfa';
import {
  signInWithPasskey,
  type PasskeyAssertionTransport,
  type PasskeySignInOptions,
} from './passkeys';
import { signInWithOrganizationSso, type OrganizationSsoTransport } from './organization-sso';
import { persistenceForRememberMe } from './session';
import { signInWithStrategy, type PopupStrategyOptions } from './strategy';
import { microsoftProvider, googleProvider } from './providers';
import { decideRoutingState } from './routing';
import { needsBackendOtpVerification } from './verification';
import type { AuthRoutingState, CompanyStatus, EmailAvailability } from './types';

/**
 * The backend the admin flows talk to (the Dart `AuthDatasource` HTTP surface, translated). The
 * api-client (03-4) supplies the concrete implementation; injecting it keeps this package free of
 * any transport coupling and makes the flows mockable.
 */
export interface AdminAuthBackend {
  /** `GET /admin/users/email_available?email=…` — is this email/domain usable to register a company. */
  checkEmailAvailable(email: string): Promise<EmailAvailability>;
  /**
   * `POST /admin/company` — register the company server-side. Resolves to a {@link CompanyRegistrationResult}:
   * `{ outcome: 'created' }` on the normal create, or `{ outcome: 'requestAccess', company }` when the
   * caller's work-email domain already belongs to an ACTIVE company and the EP-SUITE2 join-or-create
   * pivot is active (the adapter recognises the structured `COMPANY_DOMAIN_JOINABLE` 409 and returns
   * this INSTEAD of throwing — so the pivot is NOT a registration failure). Any genuine failure
   * THROWS. When the pivot is disabled the adapter always throws on error, so this only ever resolves
   * `{ outcome: 'created' }`. Called only for an account that already exists and, on the email path
   * since NF-FIX21, has proven its work email ({@link completeProviderRegistration}): a failure never
   * deletes the account.
   */
  registerCompany(params: CompanyRegistration): Promise<CompanyRegistrationResult>;
  /**
   * `POST /admin/company/resolve` — resolve the onboarding branch for a signed-in caller with a
   * VERIFIED work email (EP-SUITE2). The web calls this before the create form when join-or-create is
   * enabled, to pivot to request-access instead of creating a duplicate. Throws (typically a 403) when
   * the email is not yet server-verified; the caller falls through to the create path in that case.
   */
  resolveDomain(): Promise<OnboardingResolution>;
  /**
   * `GET /users/me` → `POST /users` — make sure the signed-in account has its Givmo `users` row
   * (idempotent per account). The backend's OTP endpoints answer 403 without one, and since NF-FIX21
   * the email sign-up proves the work email BEFORE any company exists, so it provisions the row right
   * after creating the Firebase user ({@link adminSignUpWithEmail}).
   */
  provisionUser(): Promise<void>;
  /** `POST /users/me/email_verification` — trigger the backend to send an OTP code email. */
  sendVerificationEmail(): Promise<void>;
  /** `POST /users/me/confirm_email?verification_code=…` — confirm the OTP code. Throws if invalid. */
  confirmEmailCode(code: string): Promise<void>;
  /** Fetch the signed-in user's company, or `null` if none / lookup failed. */
  getCompany(): Promise<CompanyStatus | null>;
  /**
   * Whether the user's email is verified: the Firebase record's STANDARD flag, never the persistent
   * `emailVerified` custom claim the OTP confirm also merges in (NF-FIX20 — it outlives an email change).
   */
  isEmailVerified(): Promise<boolean>;
}

/** Company registration payload (webapp `SignUpParam` → `/admin/company` body). */
export interface CompanyRegistration {
  companyName: string;
  numberOfEmployees: number;
  firstName: string;
  lastName: string;
}

/**
 * The existing ACTIVE company a request-access pivot points at — display identity ONLY (EP-SUITE2).
 * Never the roster/domain/employee count. Framework-agnostic (camelCase), mapped by the adapter from
 * the backend's `COMPANY_DOMAIN_JOINABLE` context / the resolve response's `company`.
 */
export interface CompanyJoinTarget {
  companyId: string;
  name: string;
  logoUrl: string | null;
}

/**
 * The result of a company registration attempt (EP-SUITE2 join-or-create). `created` is the normal
 * path (a new company was registered and owned). `requestAccess` is the PIVOT — the caller's verified
 * work-email domain already belongs to an ACTIVE company, so instead of a duplicate the web offers to
 * request access to `company`. The pivot is a RESOLVED value, not a thrown error, so the caller
 * routes on it rather than surfacing it as a failure.
 */
export type CompanyRegistrationResult =
  | { outcome: 'created' }
  | { outcome: 'requestAccess'; company: CompanyJoinTarget };

/**
 * The onboarding branches the resolver (`POST /admin/company/resolve`) reports for a signed-in caller
 * (EP-SUITE2). The web renders the matching next step. Kept in lockstep with the backend
 * `CompanyOnboardingOutcome` vocabulary; read as a tolerant string on the wire (unknown future values
 * route to the safe create/blocked default).
 */
export const COMPANY_ONBOARDING_OUTCOME = {
  createAsOwner: 'CREATE_AS_OWNER',
  requestAccess: 'REQUEST_ACCESS',
  alreadyMember: 'ALREADY_MEMBER',
  enableProduct: 'ENABLE_PRODUCT',
  blocked: 'BLOCKED',
} as const;

export type CompanyOnboardingOutcome =
  (typeof COMPANY_ONBOARDING_OUTCOME)[keyof typeof COMPANY_ONBOARDING_OUTCOME];

/**
 * The resolved onboarding branch for the signed-in caller. `outcome` is a {@link CompanyOnboardingOutcome}
 * value carried as a plain string (tolerant reader — an unrecognised outcome is treated as the safe
 * default by the caller, never a crash). `reason` is a stable machine code (logs / finer branching).
 * `company` carries the target's display identity ONLY, and only for the existing-company branches
 * (REQUEST_ACCESS / ALREADY_MEMBER / ENABLE_PRODUCT); it is null for CREATE_AS_OWNER / BLOCKED.
 *
 * `requestableApps` (EP-SUITE8, Org Machinery's OM-80) is the apps a REQUEST_ACCESS caller may say
 * they need, in the server's order — `[]` for every other branch — or `null` where the answer carried
 * no list (a backend before OM-80, or a malformed one). Optional so an adapter that never reads it
 * still satisfies the type; absent reads as `null`.
 */
export interface OnboardingResolution {
  outcome: string;
  reason: string;
  company: CompanyJoinTarget | null;
  requestableApps?: readonly string[] | null;
}

/** Email/password sign-in params, with the `rememberMe` persistence choice. */
export interface AdminEmailSignInParams {
  email: string;
  password: string;
  rememberMe: boolean;
}

/**
 * Email/password sign-up params. No company details: since NF-FIX21 the company is registered only
 * after the work email is proven ({@link completeProviderRegistration}, from the OTP screen).
 */
export interface AdminEmailSignUpParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/** The outcome of a sign-in/registration: a routing state plus the credential. */
export interface AdminSignInResult {
  state: AuthRoutingState;
  credential: UserCredential;
}

/** The signed-in credential each {@link NoCompanyError} stands for — see that class for why a WeakMap. */
const NO_COMPANY_CREDENTIALS = new WeakMap<NoCompanyError, UserCredential>();

/**
 * An admin sign-in whose credential WAS accepted but whose company read found none (EP-SUITE2-FIX5).
 * The caller is signed in by now. The backend port reports "this account administers no company"
 * and "the read failed" alike as a `null` company, so this is not a verdict; it is the moment an app
 * that can tell the two apart — the admin portal re-reads through its own query cache — lands a
 * company-less account on the screen its routing state names instead of failing the sign-in.
 *
 * Its kind and message are the ones this failure always carried (`unknown`, "Could not load your
 * company. Please try again."), so a caller that does not care keeps surfacing it unchanged. The
 * credential rides OFF the error, in a module-private WeakMap, for the reason `AuthError.sourceOf`
 * does: a `User` serialises its tokens, so a field would put them into `JSON.stringify` and Sentry.
 */
export class NoCompanyError extends AuthError {
  constructor(credential: UserCredential) {
    super('unknown', { message: 'Could not load your company. Please try again.' });
    this.name = 'NoCompanyError';
    // Same prototype-chain restoration as `AuthError` — `instanceof` must survive down-levelling.
    Object.setPrototypeOf(this, NoCompanyError.prototype);
    NO_COMPANY_CREDENTIALS.set(this, credential);
  }

  /** The signed-in credential a caught no-company sign-in stands for; `null` for any other value. */
  static credentialOf(error: unknown): UserCredential | null {
    return error instanceof NoCompanyError ? (NO_COMPANY_CREDENTIALS.get(error) ?? null) : null;
  }
}

/**
 * The outcome of an email/password sign-up: the Firebase credential of an account that now has its
 * Givmo `users` row and NO company. The caller proves the work email next (the OTP screen), and only
 * then registers the company (NF-FIX21) — where a request-access pivot (EP-SUITE2) can also arise.
 */
export interface AdminSignUpResult {
  credential: UserCredential;
}

// ── Email / password sign-in ─────────────────────────────────────────────────────────────────────

/**
 * Admin email/password sign-in: the shared {@link signInWithEmail} ceremony, parameterised with the
 * `rememberMe` persistence choice and the admin flow's own tail — the routing state resolved from
 * the user's company + email-verification status. The ordering the webapp port depends on
 * (persistence applied before the credential is sent) is the ceremony's, not restated here.
 *
 * Throws an `AuthError` on Firebase failure; throws a {@link NoCompanyError} (an `AuthError`, kind
 * `unknown`) for the "no company found on a non-provider sign-in" case (the cubit's `SignInFailure`
 * branch), which carries the now-signed-in credential for a caller that lands it instead. An
 * admin who enrolled TOTP on mobile gets a `SecondFactorRequiredError` instead (AS-WEB1), whose
 * challenge resumes on the identical tail once the code is accepted — `finish` is threaded into the
 * challenge rather than run after it, so both paths land on ONE routing decision.
 */
export function adminSignInWithEmail(
  auth: Auth,
  backend: AdminAuthBackend,
  params: AdminEmailSignInParams,
): Promise<AdminSignInResult> {
  return signInWithEmail(auth, params, {
    persistence: persistenceForRememberMe(params.rememberMe),
    finish: (credential) =>
      finishAdminSignIn(backend, credential, { isProviderRegistration: false }),
  });
}

// ── Passkey sign-in (AS-WEB2) ────────────────────────────────────────────────────────────────────

/**
 * Admin passkey sign-in: a WebAuthn assertion exchanged for a Firebase custom token, then the SAME
 * post-credential tail every other admin sign-in runs. Sharing `finishAdminSignIn` is the point — a
 * passkey user has the identical company + email-verification routing decision, and a second copy of
 * that logic would drift (the AS-WEB1 lesson).
 *
 * `isProviderRegistration: false`, because this is a sign-in and never a registration: a passkey can
 * only exist on an account that already registered one, so "no company found" is the same hard
 * failure it is on the email path, not an invitation to go create one.
 *
 * There is no `rememberMe` here and no persistence call, matching the provider path: the affordance
 * lives on the sign-in CHOOSER, which has no checkbox to read, so the session keeps whatever
 * persistence is already configured rather than this path silently choosing one.
 *
 * Rejects with an `AuthError` — see {@link signInWithPasskey} for the kinds.
 */
export async function adminSignInWithPasskey(
  auth: Auth,
  backend: AdminAuthBackend,
  transport: PasskeyAssertionTransport,
  options: PasskeySignInOptions = {},
): Promise<AdminSignInResult> {
  const credential = await signInWithPasskey(auth, transport, options);
  return finishAdminSignIn(backend, credential, { isProviderRegistration: false });
}

// ── Organization sign-in (AS-WEB30) ──────────────────────────────────────────────────────────────

/**
 * Admin organization sign-in: the employer's own identity provider, its handoff exchanged for a
 * custom token ({@link signInWithOrganizationSso}), then the SAME post-credential tail every other
 * admin sign-in runs — the passkey sign-in's reasoning, word for word: one routing decision, and
 * "no company found" the same hard failure it is on the email path (a `NoCompanyError` carrying the
 * now-signed-in credential, which the portal lands rather than fails). No persistence choice: the door
 * is on the chooser, which has no checkbox to read.
 *
 * Rejects with the exchange's own refusal (so the door can say which of its states it was), an
 * `AuthError` for a Firebase failure, or the tail's.
 */
export async function adminSignInWithOrganizationSso(
  auth: Auth,
  backend: AdminAuthBackend,
  transport: OrganizationSsoTransport,
  params: { handoffCode: string; codeVerifier: string },
): Promise<AdminSignInResult> {
  const credential = await signInWithOrganizationSso(auth, transport, params);
  return finishAdminSignIn(backend, credential, { isProviderRegistration: false });
}

/** Send the admin password-reset email (webapp `changePassword`). */
export async function adminSendPasswordReset(auth: Auth, email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw AuthError.from(error);
  }
}

// ── Email / password sign-up (create user → provision the row → ROLLBACK on failure) ──────────────

/**
 * Admin email/password sign-up. Creates the Firebase user, sets the display name, then provisions the
 * account's Givmo `users` row — and NOTHING ELSE. The company is registered only after the work email
 * is proven by the backend OTP (NF-FIX21): the OTP screen confirms the code, then registers through
 * {@link completeProviderRegistration}, the call the provider path has always made for an account that
 * already exists. Registering first let a sign-up abandoned at the code screen hold a real company's
 * domain, which is why the server now refuses a company for an unverified email.
 *
 * The row is provisioned here, before the OTP screen, for two reasons: the OTP endpoints answer 403
 * without it, and a provisioning refusal (an address already on a Givmo account, an account deletion
 * still in flight) must reach THIS screen with the account rolled back, exactly as a refused
 * registration used to. If provisioning FAILS, the freshly-created Firebase user is rolled back
 * (`user.delete()`, the webapp's `deleteUser()` on a non-provider failure) so a half-made account can't
 * linger and block a retry.
 */
export async function adminSignUpWithEmail(
  auth: Auth,
  backend: AdminAuthBackend,
  params: AdminEmailSignUpParams,
): Promise<AdminSignUpResult> {
  let credential: UserCredential;
  try {
    credential = await createUserWithEmailAndPassword(auth, params.email, params.password);
    await updateProfile(credential.user, {
      displayName: `${params.firstName} ${params.lastName}`,
    });
  } catch (error) {
    throw AuthError.from(error);
  }

  await provisionUserWithRollback(credential.user, backend);
  return { credential };
}

/**
 * Provision the account's Givmo `users` row and ROLL BACK the Firebase user if it FAILS. It deletes
 * the passed `user` and rethrows the original provisioning error; the delete is best-effort — if it
 * itself fails, the provisioning error still propagates (the user is informed; orphan cleanup is a
 * server concern).
 */
export async function provisionUserWithRollback(
  user: User,
  backend: AdminAuthBackend,
): Promise<void> {
  try {
    await backend.provisionUser();
  } catch (provisioningError) {
    try {
      await user.delete();
    } catch {
      // Best-effort rollback; surface the original provisioning failure regardless.
    }
    throw AuthError.from(provisioningError);
  }
}

// ── Provider (Google / Microsoft) sign-in + sign-up ───────────────────────────────────────────────

/** Build the configured admin provider. Google + Microsoft are the admin set (no Apple/Facebook). */
export type AdminProviderId = 'google' | 'microsoft';

function adminProvider(id: AdminProviderId) {
  return id === 'google' ? googleProvider() : microsoftProvider();
}

/**
 * Admin provider SIGN-IN (existing-user expectation). Popup-first. Mirrors
 * `SignInCubit.signInWith{Google,Microsoft}`:
 *   • a brand-NEW Firebase user means there's no Givmo account → ROLL BACK (`user.delete()`) and fail
 *     "not found, please sign up";
 *   • an existing user → domain pre-check, then routing decision.
 *
 * An enrolled admin rejects with a `SecondFactorRequiredError` (AS-WEB1); the challenge is
 * re-targeted here so completing the code runs the same provider tail, not a bare credential.
 */
export async function adminProviderSignIn(
  auth: Auth,
  backend: AdminAuthBackend,
  id: AdminProviderId,
  strategy: PopupStrategyOptions = {},
): Promise<AdminSignInResult> {
  let credential: UserCredential;
  try {
    credential = await signInWithStrategy(auth, adminProvider(id), strategy);
  } catch (error) {
    throw withSecondFactorChallenge(auth, error, (challenged) =>
      finishAdminProviderSignIn(auth, backend, challenged),
    );
  }
  return finishAdminProviderSignIn(auth, backend, credential);
}

/**
 * A SIGN-IN never creates an account. A Google or Microsoft sign-in that has just CREATED its Firebase
 * user — someone with no Givmo account yet — is undone (the new user deleted, so it can never pass a
 * door's auth guard) and refused with the words that send the person to sign up instead. Exported so
 * every company door runs the one rule: the employer portal's provider sign-in tail
 * (`finishAdminProviderSignIn`) and the company console's provider sign-in (EP-MNT8, D-EP-60). A
 * second-factor challenge never reaches it: an account with a factor enrolled is not new.
 */
export async function refuseNewProviderSignIn(
  auth: Auth,
  credential: UserCredential,
): Promise<void> {
  if (getAdditionalUserInfo(credential)?.isNewUser !== true) return;
  await undoNewUser(auth, credential.user);
  throw new AuthError('userNotFound', { message: 'User is not found. Please sign up.' });
}

/** What an invite's own page hands its Google or Microsoft sign-in (EP-MNT17). */
export interface ProviderInviteJoin {
  /** The address the invite is for, as the invite's preview names it. */
  invitedEmail: string;
  /** The page's refusal for any other address — its words, naming the invited one (D-EP-63). */
  otherAddressMessage: string;
}

/** Two addresses compared as the backend compares them: trimmed, lowercased. */
function sameAddress(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = (a ?? '').trim().toLowerCase();
  return left.length > 0 && left === (b ?? '').trim().toLowerCase();
}

/**
 * Join an invite with Google or Microsoft, on the invite's OWN page (EP-MNT17) — the ONE exception to
 * {@link refuseNewProviderSignIn} (D-EP-60 (4), amended by D-EP-62 (1)). Every other company door
 * undoes a first-time provider sign-in; this page exists for people new to Givmo, so a sign-in here may
 * make the account the invite is addressed to:
 *   • an account at the INVITED address is kept — a new one gets its Givmo row (rolled back with the
 *     account if the row cannot be made, as the email sign-up does); an existing one keeps its own,
 *     made idempotently and never rolled back, since it is a real account;
 *   • any OTHER address is not the invitee: a NEW account is undone (deleted, as the doors do), an
 *     EXISTING one is signed back out and never deleted (D-EP-63: "undone" read the only way it can
 *     be for an account that is not ours to delete), and both are refused with the page's words.
 * A second-factor challenge proves the account exists (a new one has nothing enrolled), which is the
 * provider sign-up's "already registered" answer: the page's sign-in link is the way on, and the door
 * completes the code. Nothing was signed in, so nothing is signed out.
 */
export async function adminProviderJoinInvite(
  auth: Auth,
  backend: AdminAuthBackend,
  id: AdminProviderId,
  invite: ProviderInviteJoin,
  strategy: PopupStrategyOptions = {},
): Promise<UserCredential> {
  let credential: UserCredential;
  try {
    credential = await signInWithStrategy(auth, adminProvider(id), strategy);
  } catch (error) {
    if (error instanceof SecondFactorRequiredError) {
      throw new AuthError('emailAlreadyInUse', {
        message: 'User is already registered. Please sign in.',
      });
    }
    throw AuthError.from(error);
  }
  const isNewUser = getAdditionalUserInfo(credential)?.isNewUser === true;
  if (!sameAddress(credential.user.email, invite.invitedEmail)) {
    if (isNewUser) await undoNewUser(auth, credential.user);
    else await auth.signOut();
    throw new AuthError('userMismatch', { message: invite.otherAddressMessage });
  }
  if (isNewUser) {
    await provisionUserWithRollback(credential.user, backend);
  } else {
    try {
      await backend.provisionUser();
    } catch (error) {
      throw AuthError.from(error);
    }
  }
  return credential;
}

/**
 * Admin provider SIGN-UP (new-user expectation). Popup-first. Mirrors
 * `SignUpCubit.signUpWith{Google,Microsoft}`: a brand-new user proceeds to finish registration; an
 * EXISTING user is signed out (NOT deleted — it's a real account) and the flow fails "already
 * registered, please sign in".
 *
 * On success returns the credential; the caller then collects company details and calls
 * `completeProviderRegistration`.
 */
export async function adminProviderSignUp(
  auth: Auth,
  id: AdminProviderId,
  strategy: PopupStrategyOptions = {},
): Promise<UserCredential> {
  let credential: UserCredential;
  try {
    credential = await signInWithStrategy(auth, adminProvider(id), strategy);
  } catch (error) {
    // A second-factor demand PROVES the account already exists — a brand-new user has nothing
    // enrolled — which on the sign-UP flow is the "already registered" outcome below, not a reason
    // to prompt for a code here. No session was established, so there is nothing to sign out of.
    if (error instanceof SecondFactorRequiredError) {
      throw new AuthError('emailAlreadyInUse', {
        message: 'User is already registered. Please sign in.',
      });
    }
    throw AuthError.from(error);
  }
  const isNewUser = getAdditionalUserInfo(credential)?.isNewUser === true;
  if (!isNewUser) {
    // Existing account hit the sign-up flow: drop the session (don't delete the real account) so it
    // doesn't leak past the auth guard, then fail.
    await auth.signOut();
    throw new AuthError('emailAlreadyInUse', {
      message: 'User is already registered. Please sign in.',
    });
  }
  return credential;
}

/**
 * Finish a registration: register the company for an account that already exists. Mirrors
 * `SignUpCubit.register(param, isProviderAuth: true)` — note that on a PROVIDER registration failure
 * the webapp does NOT delete the Firebase user (the provider account is real), so this does NOT roll
 * back; it just rethrows the registration error. Returns the {@link CompanyRegistrationResult} so a
 * request-access pivot (EP-SUITE2) surfaces here too — a belt-and-suspenders backstop, since the
 * provider path resolves the onboarding branch (verified OAuth email) BEFORE calling this.
 *
 * Since NF-FIX21 the EMAIL path registers here too, after its OTP confirmed the work email: the
 * account is verified and real by then, so it is kept on a failure exactly like a provider account.
 */
export async function completeProviderRegistration(
  backend: AdminAuthBackend,
  registration: CompanyRegistration,
): Promise<CompanyRegistrationResult> {
  try {
    return await backend.registerCompany(registration);
  } catch (error) {
    throw AuthError.from(error);
  }
}

// ── Backend-OTP email verification (custom code endpoints, NOT Firebase email links) ──────────────

/** Trigger the backend to send the OTP code email. Webapp `sendVerificationEmail`. */
export async function adminSendVerificationEmail(backend: AdminAuthBackend): Promise<void> {
  try {
    await backend.sendVerificationEmail();
  } catch (error) {
    throw AuthError.from(error);
  }
}

/** Confirm the OTP code with the backend. Webapp `verifyEmailWithCode`. Throws on an invalid code. */
export async function adminConfirmEmailCode(
  backend: AdminAuthBackend,
  code: string,
): Promise<void> {
  try {
    await backend.confirmEmailCode(code);
  } catch (error) {
    throw AuthError.from(error);
  }
}

// ── Internals ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The post-credential tail of an admin sign-in: resolve the routing state from the caller's company
 * + email-verification and pair it with the credential. Shared by the happy path and by the
 * second-factor continuation, so a challenged sign-in cannot land anywhere a clean one would not.
 */
async function finishAdminSignIn(
  backend: AdminAuthBackend,
  credential: UserCredential,
  opts: { isProviderRegistration: boolean },
): Promise<AdminSignInResult> {
  // A PROVIDER sign-in whose email the backend OTP must repair (microsoft.com — GCIP mints those
  // unverified; see verification.ts) goes to the OTP screen FIRST, before any company resolution:
  // the EP-SUITE2 onboarding endpoints refuse unverified callers, and the join detection needs a
  // trustworthy address. Deliberately provider-only — the EMAIL path's unverified-with-company case
  // is already handled by the routing decision below, and its unverified-WITHOUT-company case must
  // keep surfacing as the "could not load your company" error (routing a half-registered email user
  // through OTP would land them on screens that presume a company).
  if (opts.isProviderRegistration && needsBackendOtpVerification(credential.user)) {
    return { state: 'emailUnverified', credential };
  }
  const state = await resolveRoutingState(backend, opts);
  if (state === null) {
    throw new NoCompanyError(credential);
  }
  return { state, credential };
}

/**
 * The provider-specific tail: reject-and-roll-back a brand-new user, domain pre-check, then the
 * shared routing decision. Also shared with the second-factor continuation — a challenged credential
 * is never a new user (an unregistered one has nothing enrolled), but running the identical tail is
 * the point: the two paths cannot drift.
 */
async function finishAdminProviderSignIn(
  auth: Auth,
  backend: AdminAuthBackend,
  credential: UserCredential,
): Promise<AdminSignInResult> {
  await refuseNewProviderSignIn(auth, credential);

  await assertEmailUsableForProviderRegistration(
    backend,
    credential,
    /* isProviderRegistration */ true,
    /* isNewUser */ false,
  );
  return finishAdminSignIn(backend, credential, { isProviderRegistration: true });
}

/**
 * Domain pre-check for provider registration. Mirrors `SignInCubit._checkEmailDomain`: only when a
 * NEW user is doing a provider REGISTRATION and the backend reports the email as unavailable do we
 * reject (and roll back the new user). Existing users skip the rejection.
 */
async function assertEmailUsableForProviderRegistration(
  backend: AdminAuthBackend,
  credential: UserCredential,
  isProviderRegistration: boolean,
  isNewUser: boolean,
): Promise<void> {
  const email = credential.user.email ?? '';
  let availability: EmailAvailability;
  try {
    availability = await backend.checkEmailAvailable(email);
  } catch (error) {
    if (isNewUser) await deleteUserSafely(credential.user);
    throw AuthError.from(error);
  }
  if (availability.available === false && isNewUser && isProviderRegistration) {
    if (isNewUser) await deleteUserSafely(credential.user);
    throw new AuthError('emailAlreadyInUse', {
      message: availability.message ?? 'This email cannot be used.',
    });
  }
}

/** Resolve the routing state from the backend's company + email-verification, via the pure decision. */
async function resolveRoutingState(
  backend: AdminAuthBackend,
  opts: { isProviderRegistration: boolean },
): Promise<AuthRoutingState | null> {
  const company = await backend.getCompany();
  // `_checkUserStatus` only consults email-verification when a company exists.
  const emailVerified = company === null ? false : await backend.isEmailVerified();
  return decideRoutingState({
    company,
    emailVerified,
    isProviderRegistration: opts.isProviderRegistration,
  });
}

/**
 * Undo a brand-new account a refusal is about to answer: delete it, and when the delete left its
 * session standing (a network or SDK fault — a successful delete signs the user out), sign it out,
 * so the refusal never leaves the account it refused signed in (cut 33's review, F2). A signed-in
 * leftover would pass a door's arrival guard, and on the invite page it would be redeemed as.
 */
async function undoNewUser(auth: Auth, user: User): Promise<void> {
  await deleteUserSafely(user);
  if (auth.currentUser?.uid !== user.uid) return;
  try {
    await auth.signOut();
  } catch {
    // Best-effort, like the delete: the caller is already failing the flow.
  }
}

/** Delete a Firebase user, swallowing any delete error (best-effort rollback). */
async function deleteUserSafely(user: User): Promise<void> {
  try {
    await user.delete();
  } catch {
    // Best-effort; the caller is already failing the flow.
  }
}
